import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedScrollLockService } from '../../global/init-services';
import { WorkbenchRuntimeError } from '../../shared/errors';
import { Keys } from '../../shared/keys';
import { monkeyPatch } from '../../utils/monkey-patch';
import { querySelectorAllAsArray } from '../../utils/query-selector-all-as-array';
import { getRandomId } from '../../utils/get-random-id';
import { Size, ModalVariant } from './modal.types';
import { FALLBACK_REMOVE_TIMEOUT_MS, FOCUSABLE_SELECTORS, IS_ANIMATING, SIZES } from './modal.constants';

/**
 * @slot - one or multiple wb-modal-level elements
 */
@Component({
  tag: 'wb-modal',
  styleUrl: 'scss/modal.scss',
  shadow: true,
})
export class ModalComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Modal variant to display */
  @Prop({ reflect: true })
  public variant!: ModalVariant;

  /** Modal size */
  @Prop({ reflect: true })
  public size: Size = 'm';

  /** If using multiple levels, this prop indicates which level to display (starting at 1) */
  @Prop({ reflect: true, mutable: true })
  public activeLevel: number = 1;

  /** If present, emits wbclose event on backdrop click */
  @Prop()
  public closeOnBackdropClick?: boolean = false;

  /** If present, emits wbclose event on escape key */
  @Prop()
  public closeOnEscapeKey?: boolean = false;

  /** If present, locks scrolling of the page */
  @Prop()
  public scrollLock?: boolean = false;

  /** Modal aria-label, description provided to assistive technologies */
  @Prop({ reflect: true })
  public modalAriaLabel!: string;

  /** If present, allow to customise where the focus should go when modal is closed */
  @Prop()
  returnFocusTo?: () => HTMLElement | null;

  /** Event emitted when modal wants to be open */
  @Event()
  public wbmodalopen!: EventEmitter<null>;

  /** Event emitted when modal wants to be closed */
  @Event()
  public wbmodalclose!: EventEmitter<null>;

  /**
   * @deprecated event will be removed
   * Event emitted when modal wants to be closed
   */
  @Event()
  public wbclose!: EventEmitter<null>;

  /** Event emitted when `activeLevel` changes */
  @Event()
  public wbmodallevelchange!: EventEmitter<number>;

  /**
   * @deprecated event will be removed
   * Event emitted when `activeLevel` changes
   */
  @Event()
  public wblevelchange!: EventEmitter<number>;

  @Listen('wbmodallevelcloseclick')
  public onCloseButtonClick(event: MouseEvent): void {
    event.stopPropagation();
    this.triggerModalClose(event);
  }

  @Listen('wbmodallevelbackclick')
  public onBackButtonClick(event: MouseEvent): void {
    event.stopPropagation();

    const newLevel = Math.max(1, this.activeLevel - 1);

    if (newLevel === this.activeLevel) {
      return;
    }

    this.activeLevel = newLevel;
  }

  /** Setting capture to true prevents running this function when creating the modal as a result of a click event */
  @Listen('mousedown', { capture: true })
  public registerBackdropMouseDown(event: MouseEvent): void {
    if (this.closeOnBackdropClick === false) {
      return;
    }

    const isTargetOutsideOfDialog = !event.composedPath().includes(this.dialogEl);

    this.closeOnMouseUpOutsideOfDialog = isTargetOutsideOfDialog;
  }

  @Listen('mouseup')
  public triggerCloseOnBackdropMouseUpAndDown(event: MouseEvent): void {
    if (this.closeOnBackdropClick === false || this.closeOnMouseUpOutsideOfDialog === false) {
      return;
    }

    const isTargetOutsideOfDialog = this.dialogEl.contains(event.target as HTMLElement) === false;

    if (isTargetOutsideOfDialog) {
      this.closeOnMouseUpOutsideOfDialog = false;
      this.triggerModalClose(event, false);
    }
  }

  @Listen('keydown', { target: 'window' })
  handleKeyDown(event: KeyboardEvent) {
    const focusableElements = this.getOrderedFocusableElements();
    if (focusableElements.length === 0) {
      return;
    }

    if (event.key === Keys.ESC && this.closeOnEscapeKey) {
      event.preventDefault();
      this.triggerModalClose(event);
      return;
    }

    this.trapFocus(event);
  }

  /** Emits a wbclose event when close modal action is triggered. */
  @Method()
  public async triggerModalClose(event: MouseEvent | KeyboardEvent, preventDefault: boolean = true): Promise<void> {
    if (preventDefault) {
      event.preventDefault();
    }

    this.wbclose.emit();
    this.wbmodalclose.emit();
  }

  /**
   * Runs the modal's leave animation on demand. This method should only be used in rare cases:
   * e.g. removing the modal when using React.
   */
  @Method()
  public async runLeaveAnimation(): Promise<void> {
    return new Promise((resolve) => {
      const onTransitionEnd = (): void => {
        this.el.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      };

      this.el.addEventListener('transitionend', onTransitionEnd);
      this.el.removeAttribute('visible');
      setTimeout(onTransitionEnd, FALLBACK_REMOVE_TIMEOUT_MS); // fallback for missing transitionend event
    });
  }

  @Watch('activeLevel')
  public async onActiveLevelChange(newLevel: number, previousLevel: number): Promise<void> {
    if (this.el.hasAttribute('animating')) {
      throw new WorkbenchRuntimeError('activeLevel must not be updated during animation', 'wb-modal');
    }

    const previousLevelEl = this.el.querySelector<HTMLWbModalLevelElement>(`${this.TagNames.WbModalLevel}[level="${previousLevel}"]`);
    const nextLevelEl = this.el.querySelector<HTMLWbModalLevelElement>(`${this.TagNames.WbModalLevel}[level="${newLevel}"]`);

    // Prevent pointer-events
    this.el.classList.add(IS_ANIMATING);

    // Fade out content and hide footer
    await previousLevelEl.prepareToLeave();

    // Position the next level above the previous one
    await nextLevelEl.prepareToEnter();

    // Adjust the height of the next level
    if (this.activeLevel !== 1) {
      nextLevelEl.expandToFullHeight();
    }

    // Change the height of the previous level to the height of the next level
    const height = await nextLevelEl.getInnerHeight();
    await previousLevelEl.expandToHeight(height);

    // Remove previous level, clean up classes set during animations
    await previousLevelEl.leave();

    // Finish animation by setting active class
    await nextLevelEl.enter();

    // Allow pointer events
    this.el.classList.remove(IS_ANIMATING);

    // Emit wblevelchange
    if (newLevel !== previousLevel) {
      this.wblevelchange.emit(newLevel);
      this.wbmodallevelchange.emit(newLevel);
      this.getFirstFocusable()?.focus();
    }
  }

  private TagNames = transformTagNames(this, 'wb-modal', ['wb-grid', 'wb-grid-row', 'wb-grid-col', 'wb-modal-level']);
  private closeOnMouseUpOutsideOfDialog: boolean = false;
  private dialogEl!: HTMLElement;
  private levels: Array<HTMLWbModalLevelElement>;
  private uniqueId = getRandomId();
  private previouslyFocusedElement: HTMLElement | null = null;

  public componentDidLoad(): void {
    this.setActiveLevel();
    this.addScrollLock();
    this.runEnterAnimation();
    this.prepareLeaveAnimation();
    this.wbmodalopen.emit();

    // Get previous focused element before modal is loaded
    this.previouslyFocusedElement = this.getDeepActiveElement();

    // move focus to the first focusable element inside the modal.
    // With this way we are sure that the modal is fully rendered before calling .focus()
    requestAnimationFrame(() => {
      this.getFirstFocusable()?.focus();
    });
  }

  public disconnectedCallback(): void {
    this.removeScrollLock();

    // Restore focus to previous focused element before modal was loaded
    if (this.previouslyFocusedElement instanceof HTMLElement) {
      this.restoreFocus();
    }
  }

  private addScrollLock(): void {
    if (this.scrollLock) {
      SharedScrollLockService.lock(this.uniqueId);
    }
  }

  private removeScrollLock(): void {
    if (this.scrollLock) {
      SharedScrollLockService.unlock(this.uniqueId);
    }
  }

  private prepareLeaveAnimation(): void {
    monkeyPatch(this.el, 'remove', (done) => this.runLeaveAnimation().then(done));
  }

  private runEnterAnimation(): void {
    requestAnimationFrame(() => this.el.setAttribute('visible', ''));
  }

  private bindDialogEl = (el: HTMLWbGridColElement): void => {
    this.dialogEl = el;
  };

  private setActiveLevel = (): void => {
    this.levels = querySelectorAllAsArray<HTMLWbModalLevelElement>(this.el, this.TagNames.WbModalLevel);
    this.levels.forEach((levelEl: HTMLWbModalLevelElement) => {
      // pass down relevant props
      levelEl.variant = this.variant;
      levelEl.size = this.size;

      // show active level
      if (levelEl.level === this.activeLevel) {
        levelEl.show();
      }
    });
  };

  private handleSlotChange = (): void => {
    this.setActiveLevel();
  };

  /**
   * Returns the currently active (focused) element,
   * drilling down through shadow DOMs if necessary.
   */
  private getDeepActiveElement(root: Document | ShadowRoot = document): HTMLElement | null {
    let active = root.activeElement;

    while (active?.shadowRoot && active.shadowRoot.activeElement) {
      active = active.shadowRoot.activeElement;
    }

    return active as HTMLElement | null;
  }

  /**
   * Determines whether a given element is focusable.
   */
  private isElementFocusable = (element: Element): boolean => {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    const { visibility, display } = getComputedStyle(element);
    if (visibility === 'hidden' || display === 'none') {
      return false;
    }

    const matchesSelector = FOCUSABLE_SELECTORS.some((selector) => element.matches(selector));
    const hasTabindex = element.hasAttribute('tabindex') && element.tabIndex > -1;

    return matchesSelector || hasTabindex;
  };

  /**
   * Recursively finds all focusable elements inside a given root element.
   */
  private findFocusableDeep = (root: Element): Array<HTMLElement> => {
    const results: Array<HTMLElement> = [];

    const visit = (el: Element) => {
      if (this.isElementFocusable(el)) {
        results.push(el as HTMLElement);
      }

      if (el instanceof HTMLSlotElement) {
        el.assignedElements({ flatten: true }).forEach(visit);
      }

      if ((el as HTMLElement).shadowRoot) {
        Array.from((el as HTMLElement).shadowRoot!.children).forEach(visit);
      }

      Array.from(el.children).forEach(visit);
    };

    visit(root);
    return results;
  };

  /**
   * Collects all focusable elements inside the currently active level of the modal.
   */
  private getOrderedFocusableElements = (): Array<HTMLElement> => {
    const levelElement = this.el.querySelector<HTMLWbModalLevelElement>(`${this.TagNames.WbModalLevel}[level="${this.activeLevel}"]`).shadowRoot;

    const focusables: Array<HTMLElement> = [];

    const header = levelElement.querySelector('[part="header"]');
    if (header) {
      focusables.push(...this.findFocusableDeep(header));
    }

    for (const part of ['content', 'footer']) {
      const container = levelElement.querySelector(`[part="${part}"]`);
      if (!container) {
        continue;
      }

      const slot = container.querySelector('slot');
      if (!slot) {
        continue;
      }

      const assigned = slot.assignedElements({ flatten: true });
      for (const el of assigned) {
        focusables.push(...this.findFocusableDeep(el));
      }
    }

    return focusables;
  };

  private getFirstFocusable = (): HTMLElement | undefined => {
    return this.getOrderedFocusableElements()[0];
  };

  private getLastFocusable = (): HTMLElement | undefined => {
    const focusables = this.getOrderedFocusableElements();
    return focusables[focusables.length - 1];
  };

  private trapFocus = (event: KeyboardEvent): void => {
    const firstFocusable = this.getFirstFocusable();
    const lastFocusable = this.getLastFocusable();
    const currentActiveElement = this.getDeepActiveElement() as HTMLElement | null;

    if (event.key === Keys.TAB) {
      if (event.shiftKey) {
        if (currentActiveElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (currentActiveElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    }
  };

  private restoreFocus() {
    let elementToFocus: HTMLElement | null | undefined;

    if (this.returnFocusTo && typeof this.returnFocusTo === 'function') {
      elementToFocus = this.returnFocusTo();
    }

    if (!elementToFocus && this.previouslyFocusedElement) {
      elementToFocus = this.previouslyFocusedElement;
    }

    elementToFocus?.focus();
  }

  public render(): JSX.Element {
    const { WbGrid, WbGridRow, WbGridCol } = this.TagNames;

    return (
      //* data-on="main" is set to override top data-on="contrast" as the component has no contrast version to handle child component tokens */
      <Host class="wb-modal" data-on="main" role="dialog" aria-modal="true" aria-label={this.modalAriaLabel}>
        <WbGrid>
          <WbGridRow>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <WbGridCol class="outer" {...SIZES[this.size]} ref={this.bindDialogEl}>
              <div class="inner" tabindex="0">
                <slot onSlotchange={this.handleSlotChange} />
              </div>
            </WbGridCol>
          </WbGridRow>
        </WbGrid>
      </Host>
    );
  }
}
